const number = (value, fallback = 0) =>
    Number.isFinite(Number(value)) ? Number(value) : fallback;
const valueOf = (answers, id) => answers?.[id];

/**
 * Older AI-generated estimates sometimes create several services which all use
 * the same measurement field, but omit a way for the visitor to choose those
 * services. Add that missing choice without changing the editor's saved data.
 */
export function normalizeCalculatorConfig(config) {
    if (!config?.fields || !Array.isArray(config?.categories)) return config;

    const normalized = structuredClone(config);
    const unconditional = normalized.categories.filter(
        (category) => !category.conditions
    );
    const fieldCounts = new Map();

    unconditional.forEach((category) => {
        const seen = new Set();
        (category.items || []).forEach((item) => {
            if (item?.fieldId && !item?.options?.length) seen.add(item.fieldId);
        });
        seen.forEach((fieldId) =>
            fieldCounts.set(fieldId, (fieldCounts.get(fieldId) || 0) + 1)
        );
    });

    const [sharedFieldId, sharedFieldCount] =
        [...fieldCounts.entries()].sort((a, b) => b[1] - a[1])[0] || [];
    if (!sharedFieldId || sharedFieldCount < 2) return normalized;

    const selectableCategories = unconditional.filter((category) =>
        (category.items || []).some(
            (item) => item?.fieldId === sharedFieldId && !item?.options?.length
        )
    );
    if (selectableCategories.length < 2) return normalized;

    const existingIds = new Set(normalized.fields.map((field) => field.id));
    let selectorId = 'selectedServices';
    let suffix = 2;
    while (existingIds.has(selectorId)) {
        selectorId = `selectedServices${suffix}`;
        suffix += 1;
    }

    const selector = {
        id: selectorId,
        type: 'checkbox',
        label: 'Which services do you need?',
        options: selectableCategories.map((category) => ({
            value: category.id,
            label: category.name || category.id,
        })),
        defaultValue: selectableCategories.map((category) => category.id),
    };
    const measurementIndex = normalized.fields.findIndex(
        (field) => field.id === sharedFieldId
    );
    normalized.fields.splice(Math.max(measurementIndex, 0), 0, selector);
    selectableCategories.forEach((category) => {
        category.conditions = {
            logic: 'and',
            conditions: [
                {
                    fieldId: selectorId,
                    operator: 'contains',
                    value: category.id,
                },
            ],
        };
    });
    return normalized;
}

export function evaluateCondition(group, answers = {}) {
    if (!group) return true;
    const results = (group.conditions || []).map((condition) => {
        if (condition.group) return evaluateCondition(condition.group, answers);
        const left = valueOf(answers, condition.fieldId);
        const right = condition.value;
        switch (condition.operator) {
            case 'contains':
                return Array.isArray(left)
                    ? left.includes(right)
                    : String(left ?? '').includes(String(right));
            case 'is-selected':
                return Array.isArray(left) && left.includes(right);
            case 'has-value':
                return left !== undefined && left !== null && left !== '';
            case 'is-empty':
                return left === undefined || left === null || left === '';
            case 'greater-than':
                return number(left) > number(right);
            case 'less-than':
                return number(left) < number(right);
            case 'greater-than-or-equal':
                return number(left) >= number(right);
            case 'less-than-or-equal':
                return number(left) <= number(right);
            case 'does-not-equal':
                return left !== right;
            default:
                return left === right;
        }
    });
    if (!results.length) return true;
    return group.logic === 'or'
        ? results.some(Boolean)
        : results.every(Boolean);
}

const round = (value) =>
    Math.round((number(value) + Number.EPSILON) * 100) / 100;
const optionPrice = (option, quantity) =>
    option?.pricingType === 'per-unit'
        ? number(option.price) * quantity
        : number(option?.price);

function categorySubtotal(category, answers) {
    let total = number(category.basePrice);
    const quantity = number(answers?.[category.quantityFieldId], 1);
    for (const item of category.items || []) {
        const selected = answers?.[item.fieldId];
        const values = Array.isArray(selected) ? selected : [selected];
        for (const choice of values) {
            if (choice === undefined || choice === '' || choice === false)
                continue;
            const option = (item.options || []).find(
                (candidate) => candidate.value === choice
            );
            const itemQuantity = category.quantityFieldId
                ? quantity
                : number(choice, 1);
            total += option
                ? optionPrice(option, quantity)
                : number(item.price) *
                  (item.pricingType === 'per-unit' ? itemQuantity : 1);
        }
    }
    for (const tier of category.tiers || [])
        if (number(answers?.[tier.fieldId]) >= number(tier.minimum))
            total += number(tier.price);
    return round(total);
}

export function getVisibleFields(config, answers) {
    return (config?.fields || []).filter(
        (field) =>
            !field.visibility || evaluateCondition(field.visibility, answers)
    );
}

function conditionFieldIds(group) {
    if (!group?.conditions) return [];
    return group.conditions.flatMap((condition) => {
        if (condition?.group) return conditionFieldIds(condition.group);
        return condition?.fieldId ? [condition.fieldId] : [];
    });
}

export function validateCalculatorConfig(config) {
    const errors = [];
    const fields = config?.fields || [];
    const ids = new Set();
    fields.forEach((field) => {
        if (!field.id || ids.has(field.id))
            errors.push(
                `Duplicate or missing field ID: ${field.id || '(empty)'}`
            );
        ids.add(field.id);
    });
    fields.forEach((field, index) => {
        const earlierFieldIds = new Set(
            fields.slice(0, index).map((candidate) => candidate.id)
        );
        conditionFieldIds(field.visibility).forEach((fieldId) => {
            if (!earlierFieldIds.has(fieldId))
                errors.push(
                    `Visibility for ${
                        field.id || '(unnamed field)'
                    } must reference an earlier field`
                );
        });
    });
    const categoryIds = new Set();
    (config?.categories || []).forEach((category) => {
        if (!category.id || categoryIds.has(category.id))
            errors.push(
                `Duplicate or missing category ID: ${category.id || '(empty)'}`
            );
        categoryIds.add(category.id);
        if (category.quantityFieldId && !ids.has(category.quantityFieldId))
            errors.push(
                `Category ${category.id} references a missing quantity field`
            );
        conditionFieldIds(category.conditions).forEach((fieldId) => {
            if (!ids.has(fieldId))
                errors.push(
                    `Category ${
                        category.id || '(unnamed)'
                    } references a missing condition field`
                );
        });
    });
    (config?.rules || []).forEach((rule) =>
        conditionFieldIds(rule.conditions).forEach((fieldId) => {
            if (!ids.has(fieldId))
                errors.push(
                    `Rule ${rule.id || '(unnamed)'} references a missing field`
                );
        })
    );
    (config?.customQuoteConditions || []).forEach((conditionGroup) =>
        conditionFieldIds(conditionGroup).forEach((fieldId) => {
            if (!ids.has(fieldId))
                errors.push(
                    'Custom quote condition references a missing field'
                );
        })
    );
    if (
        !['exact', 'range', 'starting', 'custom-quote'].includes(
            config?.result?.mode
        )
    )
        errors.push(
            'Result mode must be exact, range, starting, or custom-quote'
        );
    return errors;
}

export function calculateEstimate(config, answers = {}) {
    const errors = validateCalculatorConfig(config);
    if (errors.length)
        return { status: 'invalid', errors, total: 0, categories: [] };
    const categories = (config.categories || [])
        .filter(
            (category) =>
                !category.conditions ||
                evaluateCondition(category.conditions, answers)
        )
        .map((category) => ({
            ...category,
            subtotal: categorySubtotal(category, answers),
        }));
    let adjustments = 0;
    for (const rule of [...(config.rules || [])].sort(
        (a, b) => number(a.priority) - number(b.priority)
    )) {
        if (!evaluateCondition(rule.conditions, answers)) continue;
        for (const action of rule.actions || []) {
            const target = categories.find(
                (category) => category.id === action.categoryId
            );
            if (action.type === 'fixed') adjustments += number(action.amount);
            if (action.type === 'per-unit')
                adjustments +=
                    number(action.amount) * number(answers[action.fieldId]);
            if (action.type === 'multiplier') {
                if (target)
                    target.subtotal = round(
                        target.subtotal * number(action.factor, 1)
                    );
                else
                    adjustments = round(adjustments * number(action.factor, 1));
            }
            if (action.type === 'percentage') {
                const base = target ? target.subtotal : adjustments;
                const delta = round((base * number(action.percent)) / 100);
                if (target) target.subtotal = round(base + delta);
                else adjustments += delta;
            }
            if (action.type === 'minimum')
                adjustments = Math.max(adjustments, number(action.amount));
        }
    }
    const customQuote = (config.customQuoteConditions || []).some((condition) =>
        evaluateCondition(condition, answers)
    );
    const calculatedTotal = round(
        categories.reduce((sum, category) => sum + category.subtotal, 0) +
            adjustments
    );
    const minimumJobPrice = number(config.minimumJobPrice);
    const total = round(Math.max(calculatedTotal, minimumJobPrice));
    return {
        status: customQuote ? 'custom-quote' : 'ready',
        mode: customQuote ? 'custom-quote' : config.result.mode,
        total,
        calculatedTotal,
        minimumApplied:
            minimumJobPrice > 0 && calculatedTotal < minimumJobPrice,
        low: number(config.result.range?.low),
        high: number(config.result.range?.high),
        categories,
        errors: [],
    };
}

export function createEstimatePayload(config, answers, estimate) {
    return {
        calculatorId: config?.id || '',
        calculatorName: config?.name || '',
        answers: structuredClone(answers || {}),
        estimate: structuredClone(estimate || {}),
        summary:
            estimate?.status === 'custom-quote'
                ? 'Custom quote requested'
                : `${estimate?.mode || 'exact'} estimate: ${
                      estimate?.total ?? 0
                  }`,
    };
}

export const SAMPLE_CALCULATORS = {
    windowCleaning: {
        schemaVersion: 1,
        id: 'window-cleaning',
        name: 'Residential window cleaning',
        currency: 'USD',
        minimumJobPrice: 150,
        content: {
            tagline: 'Instant estimate',
            heading: 'Get an instant window cleaning estimate',
            description:
                'Answer a few questions to see an estimated price for your home.',
            resultLabel: 'Estimated total',
            disclaimer: 'Final pricing may vary after an on-site review.',
        },
        fields: [
            {
                id: 'windows',
                type: 'quantity',
                label: 'How many windows?',
                required: true,
                min: 1,
                step: 1,
                defaultValue: 20,
            },
            {
                id: 'serviceCategory',
                type: 'select',
                label: 'What service are you looking for?',
                options: [
                    { value: 'window-cleaning', label: 'Window cleaning' },
                ],
                defaultValue: 'window-cleaning',
            },
            {
                id: 'service',
                type: 'select',
                label: 'Which window cleaning do you need?',
                options: [
                    { value: 'interior', label: 'Interior only' },
                    { value: 'exterior', label: 'Exterior only' },
                    { value: 'inside-out', label: 'Interior and exterior' },
                ],
                defaultValue: 'inside-out',
            },
            {
                id: 'stories',
                type: 'select',
                label: 'How many stories?',
                options: [
                    { value: '1', label: 'One story' },
                    { value: '2', label: 'Two or more stories' },
                ],
                defaultValue: '2',
            },
            {
                id: 'screens',
                type: 'quantity',
                label: 'How many screens?',
                min: 0,
                step: 1,
                defaultValue: 10,
            },
        ],
        categories: [
            {
                id: 'windows',
                name: 'Window cleaning',
                quantityFieldId: 'windows',
                items: [
                    {
                        fieldId: 'service',
                        options: [
                            {
                                value: 'interior',
                                price: 8,
                                pricingType: 'per-unit',
                            },
                            {
                                value: 'exterior',
                                price: 8,
                                pricingType: 'per-unit',
                            },
                            {
                                value: 'inside-out',
                                price: 12,
                                pricingType: 'per-unit',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'screens',
                name: 'Screens',
                items: [
                    { fieldId: 'screens', price: 3, pricingType: 'per-unit' },
                ],
            },
        ],
        rules: [
            {
                id: 'stories',
                name: 'Second-story adjustment',
                priority: 10,
                conditions: {
                    conditions: [
                        { fieldId: 'stories', operator: 'equals', value: '2' },
                    ],
                },
                actions: [
                    { type: 'multiplier', categoryId: 'windows', factor: 1.2 },
                ],
            },
        ],
        customQuoteConditions: [
            {
                conditions: [
                    { fieldId: 'windows', operator: 'greater-than', value: 40 },
                ],
            },
        ],
        result: { mode: 'exact' },
        actions: {
            primaryText: 'Book your cleaning',
            primaryLink: '',
            primaryIcon: '',
            primaryIconVisible: true,
            primaryOpenInNewTab: false,
            secondaryText: 'Call us',
            secondaryLink: '',
            secondaryIcon: '',
            secondaryIconVisible: true,
            secondaryOpenInNewTab: false,
        },
        leadCapture: { enabled: false },
    },
    pressureWashing: {
        schemaVersion: 1,
        id: 'house-washing',
        name: 'House washing estimate',
        currency: 'USD',
        minimumJobPrice: 199,
        content: {
            heading: 'Estimate your house washing project',
            resultLabel: 'Estimated total',
        },
        fields: [
            {
                id: 'sqft',
                type: 'square-footage',
                label: 'Approximate square footage',
                defaultValue: 1200,
            },
        ],
        categories: [
            {
                id: 'wash',
                name: 'House washing',
                quantityFieldId: 'sqft',
                items: [
                    { fieldId: 'sqft', price: 0.25, pricingType: 'per-unit' },
                ],
            },
        ],
        rules: [],
        customQuoteConditions: [],
        result: { mode: 'exact' },
        actions: { primaryText: 'Request a quote', primaryLink: '' },
        leadCapture: { enabled: false },
    },
    renovation: {
        schemaVersion: 1,
        id: 'renovation-estimate',
        name: 'Renovation estimate',
        currency: 'USD',
        content: {
            heading: 'Get a renovation budget range',
            resultLabel: 'Estimated range',
        },
        fields: [
            {
                id: 'projectType',
                type: 'select',
                label: 'What are you renovating?',
                options: [
                    { value: 'kitchen', label: 'Kitchen' },
                    { value: 'bathroom', label: 'Bathroom' },
                ],
                defaultValue: 'kitchen',
            },
        ],
        categories: [],
        rules: [],
        customQuoteConditions: [],
        result: { mode: 'range', range: { low: 8000, high: 15000 } },
        actions: {
            primaryText: 'Discuss your project',
            primaryLink: '',
        },
        leadCapture: { enabled: false },
    },
    roofing: {
        schemaVersion: 1,
        id: 'roofing-estimate',
        name: 'Residential roofing estimate',
        industry: 'Roofing',
        service: 'Roof replacement',
        currency: 'USD',
        minimumJobPrice: 2500,
        content: {
            tagline: 'Instant estimate',
            heading: 'Estimate your roofing project',
            description:
                'Choose your roof type and answer a few relevant questions for an estimated project price.',
            resultLabel: 'Estimated total',
            disclaimer:
                'Final pricing depends on an on-site roof inspection and material availability.',
            customQuoteMessage:
                'This roof needs an in-person assessment. Request a detailed quote.',
        },
        fields: [
            {
                id: 'roofType',
                type: 'select',
                label: 'What type of roof do you have?',
                required: true,
                options: [
                    { value: 'shingle', label: 'Shingle roof' },
                    { value: 'flat', label: 'Flat roof' },
                    { value: 'metal', label: 'Metal roof' },
                ],
                defaultValue: 'shingle',
            },
            {
                id: 'roofArea',
                type: 'square-footage',
                label: 'Approximate roof area (sq. ft.)',
                required: true,
                min: 250,
                step: 50,
                defaultValue: 2000,
            },
            {
                id: 'shingleGrade',
                type: 'select',
                label: 'Which shingle grade would you like?',
                options: [
                    { value: 'architectural', label: 'Architectural shingle' },
                    { value: 'premium', label: 'Premium designer shingle' },
                ],
                defaultValue: 'architectural',
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'shingle',
                        },
                    ],
                },
            },
            {
                id: 'removeExistingShingles',
                type: 'boolean',
                label: 'Do existing shingles need to be removed?',
                defaultValue: false,
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'shingle',
                        },
                    ],
                },
            },
            {
                id: 'flatMembrane',
                type: 'select',
                label: 'Which flat-roof membrane would you like?',
                options: [
                    { value: 'tpo', label: 'TPO membrane' },
                    { value: 'epdm', label: 'EPDM rubber membrane' },
                ],
                defaultValue: 'tpo',
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'flat',
                        },
                    ],
                },
            },
            {
                id: 'drainageAreas',
                type: 'quantity',
                label: 'How many drainage areas need work?',
                min: 0,
                step: 1,
                defaultValue: 2,
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'flat',
                        },
                    ],
                },
            },
            {
                id: 'metalStyle',
                type: 'select',
                label: 'Which metal roof style do you have?',
                options: [
                    { value: 'standing-seam', label: 'Standing seam' },
                    { value: 'corrugated', label: 'Corrugated panel' },
                ],
                defaultValue: 'standing-seam',
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'metal',
                        },
                    ],
                },
            },
            {
                id: 'rustRepair',
                type: 'boolean',
                label: 'Does the roof need rust repair?',
                defaultValue: false,
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'metal',
                        },
                    ],
                },
            },
        ],
        categories: [
            {
                id: 'shingle-roof',
                name: 'Shingle roof replacement',
                quantityFieldId: 'roofArea',
                conditions: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'shingle',
                        },
                    ],
                },
                items: [
                    {
                        fieldId: 'shingleGrade',
                        options: [
                            {
                                value: 'architectural',
                                price: 6,
                                pricingType: 'per-unit',
                            },
                            {
                                value: 'premium',
                                price: 8,
                                pricingType: 'per-unit',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'shingle-removal',
                name: 'Existing shingle removal',
                quantityFieldId: 'roofArea',
                conditions: {
                    logic: 'and',
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'shingle',
                        },
                        {
                            fieldId: 'removeExistingShingles',
                            operator: 'equals',
                            value: true,
                        },
                    ],
                },
                items: [
                    {
                        fieldId: 'roofArea',
                        price: 1.5,
                        pricingType: 'per-unit',
                    },
                ],
            },
            {
                id: 'flat-roof',
                name: 'Flat roof replacement',
                quantityFieldId: 'roofArea',
                conditions: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'flat',
                        },
                    ],
                },
                items: [
                    {
                        fieldId: 'flatMembrane',
                        options: [
                            { value: 'tpo', price: 7, pricingType: 'per-unit' },
                            {
                                value: 'epdm',
                                price: 8,
                                pricingType: 'per-unit',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'flat-drainage',
                name: 'Drainage work',
                conditions: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'flat',
                        },
                    ],
                },
                items: [
                    {
                        fieldId: 'drainageAreas',
                        price: 250,
                        pricingType: 'per-unit',
                    },
                ],
            },
            {
                id: 'metal-roof',
                name: 'Metal roof replacement',
                quantityFieldId: 'roofArea',
                conditions: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'metal',
                        },
                    ],
                },
                items: [
                    {
                        fieldId: 'metalStyle',
                        options: [
                            {
                                value: 'standing-seam',
                                price: 11,
                                pricingType: 'per-unit',
                            },
                            {
                                value: 'corrugated',
                                price: 9,
                                pricingType: 'per-unit',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'metal-rust-repair',
                name: 'Rust repair allowance',
                conditions: {
                    logic: 'and',
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'metal',
                        },
                        {
                            fieldId: 'rustRepair',
                            operator: 'equals',
                            value: true,
                        },
                    ],
                },
                items: [
                    { fieldId: 'rustRepair', price: 800, pricingType: 'fixed' },
                ],
            },
        ],
        rules: [],
        customQuoteConditions: [
            {
                conditions: [
                    {
                        fieldId: 'roofArea',
                        operator: 'greater-than',
                        value: 5000,
                    },
                ],
            },
        ],
        result: { mode: 'exact' },
        actions: {
            primaryText: 'Request a roofing quote',
            primaryLink: '',
            primaryIcon: '',
            primaryIconVisible: true,
            primaryOpenInNewTab: false,
            secondaryText: 'Call us',
            secondaryLink: '',
            secondaryIcon: '',
            secondaryIconVisible: true,
            secondaryOpenInNewTab: false,
        },
        leadCapture: { enabled: false },
    },
};

function cloneTemplate(config, id, name, industry, service) {
    return {
        ...structuredClone(config),
        id,
        name,
        industry,
        service,
    };
}

export const CALCULATOR_TEMPLATES = [
    {
        id: 'general-service',
        label: 'General service estimate',
        industry: 'General',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.windowCleaning,
            'general-service',
            'Service estimate',
            'General',
            'Service'
        ),
    },
    {
        id: 'window-cleaning',
        label: 'Residential window cleaning',
        industry: 'Window cleaning',
        config: SAMPLE_CALCULATORS.windowCleaning,
    },
    {
        id: 'house-washing',
        label: 'House washing',
        industry: 'Pressure washing',
        config: SAMPLE_CALCULATORS.pressureWashing,
    },
    {
        id: 'driveway-washing',
        label: 'Driveway pressure washing',
        industry: 'Pressure washing',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.pressureWashing,
            'driveway-pressure-washing',
            'Driveway pressure washing estimate',
            'Pressure washing',
            'Driveway washing'
        ),
    },
    {
        id: 'interior-painting',
        label: 'Interior painting',
        industry: 'Painting',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.renovation,
            'interior-painting',
            'Interior painting estimate',
            'Painting',
            'Interior painting'
        ),
    },
    {
        id: 'landscaping',
        label: 'Landscaping',
        industry: 'Landscaping',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.pressureWashing,
            'landscaping',
            'Landscaping estimate',
            'Landscaping',
            'Landscaping'
        ),
    },
    {
        id: 'plumbing',
        label: 'Plumbing service call',
        industry: 'Plumbing',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.windowCleaning,
            'plumbing-service-call',
            'Plumbing service call estimate',
            'Plumbing',
            'Service call'
        ),
    },
    {
        id: 'hvac',
        label: 'HVAC maintenance',
        industry: 'HVAC',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.windowCleaning,
            'hvac-maintenance',
            'HVAC maintenance estimate',
            'HVAC',
            'Maintenance'
        ),
    },
    {
        id: 'roofing',
        label: 'Roof replacement',
        industry: 'Roofing',
        config: SAMPLE_CALCULATORS.roofing,
    },
    {
        id: 'renovation',
        label: 'Renovation estimate',
        industry: 'Renovations',
        config: SAMPLE_CALCULATORS.renovation,
    },
];
